import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, rupiah, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { PendingPaymentBanner } from "@/components/PendingPaymentBanner";
import { Wallet, PiggyBank, AlertCircle, GraduationCap, Receipt, User } from "lucide-react";

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/parent/students").then((r) => setStudents(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader title={`Assalamu'alaikum,`} subtitle={`${user?.name} — berikut data & tagihan anak Anda.`} />

      <PendingPaymentBanner />

      {loading ? (
        <p className="text-stone-400">Memuat…</p>
      ) : students.length === 0 ? (
        <Empty><GraduationCap className="mx-auto mb-2" /> Belum ada santri terhubung dengan akun Anda.</Empty>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {students.map((s, i) => (
            <div
              key={s.id}
              data-testid={`student-card-${s.id}`}
              className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-stone-100 border flex items-center justify-center shrink-0">
                  {s.photo_path
                    ? <img src={fileUrl(s.photo_path)} alt={s.name} className="w-full h-full object-cover" />
                    : <User size={30} className="text-stone-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold text-stone-900 text-lg leading-tight">{s.name}</p>
                  <div className="mt-1 space-y-0.5 text-xs text-stone-500">
                    <p>NISN: <span className="font-mono text-stone-700">{s.nisn}</span></p>
                    <p>Unit: <span className="capitalize">{s.gender}</span> · Angkatan {s.angkatan}</p>
                  </div>
                </div>
              </div>

              {s.outstanding > 0 ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-rose-600 bg-rose-50 rounded-xl px-3 py-2">
                  <AlertCircle size={16} /> Tunggakan {rupiah(s.outstanding)} ({s.unpaid_bills} tagihan)
                </div>
              ) : (
                <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">
                  ✓ Semua tagihan lunas
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-stone-50 border p-3">
                  <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><Wallet size={12} /> Uang Jajan</p>
                  <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(s.personal_balance)}</p>
                </div>
                <div className="rounded-xl bg-stone-50 border p-3">
                  <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><PiggyBank size={12} /> Tabungan</p>
                  <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(s.savings_balance)}</p>
                </div>
              </div>

              <Button
                onClick={() => navigate("/parent/tagihan")}
                data-testid={`tagihan-button-${s.id}`}
                className="w-full mt-4 rounded-xl bg-emerald-600 hover:bg-emerald-700"
              >
                <Receipt size={17} className="mr-2" /> Tagihan
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
